class Customer.Ticket extends Backbone.Model

  MAX_INTERACTIONS_REVERSE_ITERATION: '20'

  urlRoot: '/customer/conversations'

  default:
    loaded: false
    notes: []

  initialize: (attributes, @tags) ->
    super attributes
    @initializeNotes()
    @initializeVisibleEvents()
    @_initializeStatusEvents()
    @initializeStats()
    @initializeAsyncNotes()
    @initializeIntegrations()

  parse: (response, options) ->
    unless response.id
      jsLogger.fatal("Ticket model data is missing id. Response: #{JSON.stringify(response)}")
    response.loaded = true
    if response.notes
      updatedNotes = @get('notes')
      updatedNotes.set(new Customer.NotesCollection(response.notes, {conversation_id: @id}).models)
      response.notes = updatedNotes
    if response.integrations
      updatedIntegrations = @get('integrations')
      updatedIntegrations.get('salesforce').set(response.integrations.salesforce)
      updatedIntegrations.get('patagonia').set(response.integrations.patagonia)
      response.integrations = updatedIntegrations
    if response.webchat_widget_closed_event
      updatedEvents = @get('events')
      updatedEvents.set(new Customer.EventsCollection([response.webchat_widget_closed_event], {conversation_id: @id}).models)
      response.events = updatedEvents
    if response.visible_events
      updatedVisibleEvents = @get('visible_events')
      updatedVisibleEvents.set(new Customer.VisibleEventCollection([response.visible_events], {conversation_id: @id}).models)
      response.visible_events = updatedVisibleEvents
    if response.status_events
      response.status_events = @_setStatusEvents(response.status_events)
    return response

  initializeIntegrations: =>
    @set('integrations', new Customer.Ticket.Integrations(@get('integrations'), ticket: @))

  initializeNotes: =>
    @set('notes', new Customer.NotesCollection(@get('notes') || [], {conversation_id: @id}))
    @listenTo(@get('notes'), 'add', => @trigger('add:note', @get('notes')))

  initializeVisibleEvents: =>
    @set('visible_events', new Customer.VisibleEventCollection([], {conversation_id: @id}))
    @listenTo(@get('visible_events'), 'add', => @trigger('add:visible_event', @get('visible_events')))

  _initializeStatusEvents: ->
    @set('status_events', new Customer.Ticket.ConversationHistoryEventCollection([]))

  initializeStats: =>
    @stats = undefined # These will be loaded on demand

  initializeAsyncNotes: =>
    @asyncNotes = undefined # These will be loaded on demand

  summaryInteraction: ->
    interaction = @dialogue().summary_interaction
    if interaction == null
      return undefined
    if !(interaction instanceof Backbone.Model)
      try
        interaction = new Monitoring.Models.SocialContents.AnalysisUnitFactory().create(interaction, @tags)
      catch error
        jsLogger.fatal("Ticket summary can't be initialized: #{JSON.stringify interaction} - #{error.stack}")
        console.log error.message
        return undefined
    interaction

  getFirstItem: ->
    if @distinguishedContent()
      return @distinguishedContent()
    else
      interactions = @interactions()
      ret = _.find(interactions, (interaction) -> !interaction.isNotFound?()) || interactions[0]
      if ret
        ret
      else
        throw new Error("Could not access the first interaction of the ticket #{@id}")

  getLastItem: ->
    if @isFacebookPost() or @isInstagramMedia()
      @getFirstItem()
    else
      interactions = @interactions()
      interactions[interactions.length - 1]

  getInteractionToResponse: ->
    if @isWhatsapp()
      sort = -1
      for interaction in @interactions() by sort
        if not interaction.isBrandContent()
          return interaction
    if (@isInstagramMedia() or @isGooglePlay()) and @distinguishedContent()
      return @distinguishedContent()
    if (@isFacebookPost())
      if (@interactions().length > 0)
        return _.first(@interactions())
      else
        return @distinguishedContent()
    sort = -1
    for interaction in @interactions() by sort
      if not interaction.get('responseInteraction')
        return interaction

  getInteractionIdToResponse: ->
      @getInteractionToResponse().get('interaction_id')

  # This is temporary, it will most likely be replaced with a method that
  # returns an array of relevant interactions for response template logic.
  # Difference with getInteractionToResponse:
  # in a facebook message thread, users can only reply to the last one,
  # so we want the tags of the last content to be the relevant ones for
  # response templates, as opposed to that being dependent on the dialogueOrder
  getInteractionForResponseTemplates: ->
    return @distinguishedContent() if @canResponseTo()

    sort = -1
    for interaction in @interactions() by sort
      return interaction if @canResponseTo(interaction)

  canResponseTo: (interaction) ->
    @_canResponseToDistinguishedContent() || @_validInteraction(interaction)

  _validInteraction: (interaction) ->
    interaction && !interaction.get('responseInteraction') && !interaction.isResponseFromClient()

  _canResponseToDistinguishedContent: ->
    value = @distinguishedContent() && !(@distinguishedContent().isBrandContent())
    (@isFacebookPost() || @isInstagramMedia() || @isGooglePlay()) && value

  _valueToBoolean: (value) ->
    value && (value == true || value == 'true')

  getInteractionById: (interactionId) ->
    _.find(@allInteractions(), (interaction) => interaction.get('interaction_id') == interactionId)

  allInteractions: ->
    _.compact(_.union(@interactions(), [@distinguished_content]))

  firstPrivateNonBrandInteraction: ->
    _.find(@allInteractions(), (i) =>
      i.privacy() == 'private' && !i.isBrandContent() && !i.isNotFound?()
    )

  getResponseScheduleById: (interaction) ->
    _.find(@interactions(), (i) => (i.id == interaction.id) || (@getInteractionId(i) == @getInteractionId(interaction)))

  getInteractionId: (interaction) ->
    interaction.data?.content?.id  || interaction.attributes?.data?.content?.id

  getInteractionByContentId: (contentId) ->
    _.find(@interactions(), (interaction) => interaction.numeric_id() == contentId)

  getPresentInteractionByContentId: (contentId) ->
    _.find(@interactions(), (interaction) => interaction.attributes?.content?.getContentId() == contentId && interaction.attributes.responseInteraction == undefined )

  getPresentResponseInteractionByContentId: (contentId) ->
    _.find(@interactions(), (interaction) => interaction.attributes?.content?.getContentId() == contentId && interaction.attributes.responseInteraction == true )

  participants: ->
    @dialogue().participants

  contextParticipantsCount: ->
    @get('context_participants_count')

  socialAccounts: ->
    @dialogue().social_accounts

  visible_events: ->
    @get('visible_events')

  isTwitter: ->
    @type() == 'twitter'

  isEmail: ->
    @type() == 'email'

  isFacebook: ->
    @type() == 'facebook'

  isInstagram: (interactionId = null) ->
    @isInstagramPublic(interactionId) || @isInstagramPrivate(interactionId)

  isInstagramPrivate: (interactionId = null) ->
    _.contains ['instagram'], @type(interactionId)

  isInstagramPublic: (interactionId = null) ->
    _.contains ['instagramMedia', 'instagramComment'], @type(interactionId)

  isFacebookPost: ->
    @isFacebook() and @subType() == 'fb-post'

  isFacebookComment: ->
    @isFacebook() and @subType() == 'fb-comment'

  isFacebookMessage: ->
    @isFacebook() and @subType() == 'fb-message'

  isInstagramMedia: ->
    @type() == 'instagramMedia'

  isYoutube: ->
    @type() == 'youtube'

  isWebchat: ->
    @type() == 'webchat'

  isWhatsapp: ->
    @type() == 'whatsapp'

  isReclameAqui: ->
    @type() == 'reclameaqui'

  isGooglePlay: ->
    @type() == 'google_play'

  isAppStore: ->
    @type() == 'apple_app_store'

  isLinkedIn: ->
    @type() == 'linkedin'

  isGoogleMyBusiness: ->
    @type() == Monitoring.Models.SocialContents.GoogleMyBusiness.GoogleMyBusinessContent.TYPE

  isByUser: ->
    /user$/.test(@ownSubtype())

  isWebchatFinalized: ->
    @isWebchat() && !_.isEmpty(@visible_events().where({sub_type: 'webchat_finalized'}))

  groupType: ->
    @getFirstItem().groupType()

  type: (interactionId = null) ->
    return @getFirstItem().type() if interactionId == null

    @getInteractionById(interactionId).type()

  properType: ->
    ret = @get('type') || @type()
    ret = 'instagram' if (/instagram/.test(ret))
    ret

  subType: ->
    @getFirstItem().subType()

  ownSubtype: ->
    @get('sub_type')

  dialogue: ->
    return @get('dialogue')

  priority: ->
    return @get('priority')

  dialogueOrder: ->
    @get('dialogue').order

  distinguishedContent: ->
    return unless @get('dialogue')['distinguished_content'] || @get('parent_distinguished_interaction')
    return @distinguished_content if @distinguished_content
    try
      contentToUse = @get('dialogue')['distinguished_content'] || @get('parent_distinguished_interaction')
      @distinguished_content = new Monitoring.Models.SocialContents.AnalysisUnitFactory().create(contentToUse, @tags)
    catch error
      jsLogger.fatal("Ticket create distinguished content: #{JSON.stringify contentToUse} - #{error.stack}")
      console.log error.message
    @distinguished_content

  hasMore: ->
    !!@get('dialogue')['has_more']

  hasFooterReplyBox: ->
    @get('footer_reply_box')

  hasMoreContextInteractions: ->
    @lastContextInteractionsFetched.has_more

  loadMore: ->
    @page = @page || 1
    @page += 1
    $.get("#{@urlRoot}/#{@id}.json", {page: @page.toString()}, @parseMoreContent)

  fetchNewInteractions: =>
    return if @dialogueOrder() == 'asc' && @hasMore()
    if _.isEmpty(@interactions())
      firstInteractionDatetime = moment.utc(Date.create(@get('created_at')))
    else
      firstInteractionDatetime = moment.utc(@_date(@_firstShowedInteraction()))
    unless firstInteractionDatetime.isValid()
      firstInteractionDatetime = moment()
      firstInteractionDatetime = moment.utc(firstInteractionDatetime).minutes(firstInteractionDatetime.minutes() - 60)

    excludedInteractions = _.pluck (_.pluck @allInteractions(), 'attributes'), 'interaction_id'
    if @isWhatsapp()
      excludedInteractions = []
    else
      excludedInteractions = excludedInteractions.filter (interactionId) -> interactionId != undefined && interactionId.toString().trim() != ''

    url = "/customer/ticket/new_interactions"
    params = {
      id: @id,
      excluded_interactions: excludedInteractions,
      limit_datetime: firstInteractionDatetime.format('YYYY-MM-DD HH:mm:ss.S')
    }
    $.get(url, params, @updateWithJson)

  parseContent: (response) =>
    @newInteractions = @parseResponseContent(response.dialogue.interactions)
    newStatusEvents = @_parseNewStatusEvents(response.status_events)
    if @newInteractions
      dialogue = @get('dialogue')
      dialogue.interactions = dialogue.interactions.concat @newInteractions
      dialogue.has_more = response.dialogue.has_more
      @set('dialogue', dialogue)
      toRender = @addNotesToRender(@newInteractions)
      return toRender unless newStatusEvents
      @_setNewStatusEvents(newStatusEvents)
      toRender = toRender.concat(@_serializeStatusEvents(newStatusEvents))
      @_sortedInteractionsWithNewerLast(toRender)

  parseMoreContent: (response) =>
    toRender = @parseContent(response)
    @trigger('ticket:moreLoaded', toRender) if toRender

  parseNewContent: (response) =>
    toRender = @parseContent(response)
    @trigger('ticket:newLoaded', toRender, response.ticket_lock_version) if toRender

  loadContextInteractions: ->
    $.get("#{@urlRoot}/#{@id}/context_interactions", { page: 1, page_size: 10 }, @parseContextInteractions)

  loadMoreContextInteractions: ->
    @contextInteractionsPage = @contextInteractionsPage || 1
    @contextInteractionsPage += 1
    $.get("#{@urlRoot}/#{@id}/context_interactions", { page: @contextInteractionsPage, page_size: 10 }, @parseMoreContextInteractions)

  parseContextInteractions: (response) =>
    @lastContextInteractionsFetched = response
    @loadedContextInteractions = @parseResponseContent(response.interactions)
    @trigger('ticket:contextInteractionsLoaded', @loadedContextInteractions)

  parseMoreContextInteractions: (response) =>
    @lastContextInteractionsFetched = response
    contents = @parseResponseContent(response.interactions)
    @trigger('ticket:moreContextInteractionsLoaded', contents)

  parseResponseContent: (response) =>
    try
      interactions = _.map response, (interaction) =>
        socialContent = new Monitoring.Models.SocialContents.AnalysisUnitFactory().create(interaction, @tags)
        socialContent.viewType = 'interaction'
        socialContent
    catch error
      jsLogger.fatal("Can't parse ticket page response: #{JSON.stringify response} - #{error.stack}")
      console.log error.message
      interactions = false
      showError("Can't show the ticket content")
    interactions

  addNotesToRender: (newInteractions) ->
    newLastInteraction = newInteractions[newInteractions.length-1]
    toRender = newInteractions
    _this = @
    notesToShow = _.filter(@notesLeft, (note) -> _this._date(note) >= _this._date(_this._firstShowedInteraction()))
    _notesLeft = _.difference(@notesLeft, notesToShow)
    if @hasMore()
      for note in notesToShow
        if @_date(note) < @_date(newLastInteraction)
          toRender.push note
        else
          _notesLeft.push note
    else
      toRender = toRender.concat notesToShow if notesToShow
    @notesLeft = _notesLeft
    @_sortedInteractionsWithNewerLast(toRender)

  interactions: ->
    # TODO: Move initialization and sorting to another method (if possible)
    if _.any(@dialogue().interactions, (interaction) -> !(interaction instanceof Backbone.Model))
      @dialogue().interactions = _.map @dialogue().interactions, (interaction) =>
        socialContent = new Monitoring.Models.SocialContents.AnalysisUnitFactory().create(interaction, @tags)
        [socialContent].concat(socialContent.childrenSocialContents(@tags))
      @dialogue().interactions = @dialogue().interactions.flat()
    return @dialogue().interactions

  interactionsToRender: ->
    interactions = @sorted_interactions(@interactions())
    for interaction in interactions
      interaction.viewType = 'interaction'
    notes = @get('notes').map (note) ->
      note.viewType = 'note'
      note
    interactions = interactions.concat @_filteredNotes(notes, interactions)
    visible_events = @get('visible_events').map (_event) ->
      _event.viewType = 'visible_event'
      _event
    interactions = interactions.concat (visible_events)
    statusEvents = @_serializeStatusEvents(@get('status_events'))
    toRender = interactions.concat(statusEvents)
    @sorted_interactions(toRender)

  sorted_interactions: (toRender) ->
    # When two interactions have exactly the same date, we use its id as a tiebreak
    toRender = _(toRender).chain()
      .sortBy((object) => object.get('interaction_id') || object.get('id'))
      .sortBy((object) => @_date(object))
      .value()
    toRender

  _sortedInteractionsWithNewerLast: (toRender) ->
  # This sorting is used in WhatsApp, to show new interactions from clients last so operator will notice them, intead of resorting them all.
    currentDatetime = moment()
    thirtySecondsAgo = currentDatetime.seconds(currentDatetime.seconds() - 30)
    toRender = _(toRender).chain()
      .sortBy((object) => object.get('interaction_id') || object.get('id'))
      .sortBy((object) => @_date(object))
      .sortBy((object) => object.viewType == 'interaction' &&
        !object.isBrandContent() && @_date(object) > thirtySecondsAgo)
      .value()
    toRender

  _firstShowedInteraction: ->
    @sorted_interactions(@interactions())[0]

  _filteredNotes: (notes, interactions) ->
    return notes unless @hasMore()
    [first_interaction, ..., lastInteraction] = interactions
    filteredNotes = []
    @notesLeft = []
    for note in notes
      if @_isFilteredNote(note, first_interaction, lastInteraction)
        filteredNotes.push note
      else
        @notesLeft.push note
    filteredNotes

  _isFilteredNote: (note, first_interaction, lastInteraction) ->
    @_date(note) > @_date(first_interaction)

  hasActiveParticipants: ->
    @get('active_user')?

  activeParticipants: ->
    activeUserId = @get('active_user').split('-')
    activeUserId.splice(0, 1)
    activeUserId.join('-')

  isAssigned:->
    @get('status') == 'Assigned' && (@get('assigned_to_id') || @get('assigned_operator_id') ||  @get('assigned_to_robot'))

  hasPublicTextLimit: ->
    @publicTextLimit() # undefined (false) or a number (true)

  publicTextLimit: ->
    @dialogue().public_reply_chars_limit

  hasPrivateTextLimit: ->
    @privateTextLimit() # undefined (false) or a number (true)

  privateTextLimit: ->
    @dialogue().private_reply_chars_limit

  admitsPublicReply: ->
    @dialogue().admits_public_reply

  admitsPrivateReply: ->
    @dialogue().admits_private_reply

  _date: (obj) ->
    Date.create(obj.created_at())

  toJSON: ->
    json = super()
    @_addFields(json)
    return json

  asJson: -> # Viene con el Hack en ticketView.js#saveTicket
    json = @attributes
    @_addFields(json)
    return json

  _addFields: (obj) ->
    # All content types that can be replied to must be listed here
    obj.can_reply = obj.can_reply && _.some([@isTwitter(), @isFacebook(), @isEmail(), @isInstagram(), @isYoutube(),
      @isWebchat(), @isReclameAqui(), @isGooglePlay, @isAppStore(), @isWhatsapp(), @isLinkedIn()])
    obj.group_type = @groupType()
    obj.type = @type()
    obj.proper_type = @properType()
    obj.admits_public_reply = @admitsPublicReply()
    obj.admits_private_reply = @admitsPrivateReply()
    obj.has_public_text_limit = @hasPublicTextLimit()
    obj.public_text_limit = @publicTextLimit()
    obj.has_private_text_limit = @hasPrivateTextLimit()
    obj.private_text_limit = @privateTextLimit()
    obj.context_participants_count = @contextParticipantsCount() || 0
    obj.is_by_user = @isByUser()

  updateAssignedToId: (value) ->
    @set('assigned_to_id', value)

  updateUsersGroupId: (value) ->
    @set('users_group_id', value)

  updateLockVersion: (value) ->
    @set('lock_version', value)

  updateStatus: (value) ->
    @set('status', value)

  updateAssignedToName: (value) ->
    @set('assigned_to_name', value)

  updateCanUpdate: (value) ->
    @set('can_update', value)

  updateCanReply: (value) ->
    @set('can_reply', value)

  updateCanClose: (value) ->
    @set('can_close', value)

  updateUserAppointment: (value) ->
    @set('userAppointment', value)

  assignTicket: (userId, groupId) ->
    params = assigned_to_id: userId, users_group_id: groupId, id: this.get('id')
    $.ajax
      type: 'POST'
      url: '/customer/ticket/assign'
      data: params
      success: (response) =>
        if response.success
          @_onUpdateTicketSuccess(response)
          @updateAssignedToId(response.assigned_to_id)
          @updateUsersGroupId(response.users_group_id)
          @trigger('ticket:assign:success')
          @updateMetrics(response)
        else
          @trigger('ticket:assign:error', response)
      error: (response) =>
        @trigger('ticket:assign:error', response)

  selfAssignClosedTicket: () ->
    params = id: this.get('id')
    $.ajax
      type: 'POST'
      url: '/customer/ticket/self_assign_closed'
      data: params
      success: (response) =>
        if response.success
          @_onUpdateTicketSuccess(response)
          @updateAssignedToId(response.assigned_to_id)
          @updateUsersGroupId(response.users_group_id)
          @trigger('ticket:assign:success')
        else
          @trigger('ticket:saved:error', response)
      error: (response) =>
        @trigger('ticket:saved:error', response)

  saveTicket: (attributes, onSuccess, onError) ->
    # TODO: Este hack es porque backbone no soporta correctamente enviar solo los attributos modificados (ademas habria q mandar el lock version)
    toJsonMethod = @toJSON
    json = @toJSON()
    json['dialogue'] = null
    @toJSON = => return _.extend({}, json, attributes)
    @save(
      attributes,
      wait: true,
      global: false,
      success: (model, response) =>
        if response.success
          @_onUpdateTicketSuccess(response)
          @trigger('ticket:saved:success')
          if _.isFunction(onSuccess)
            if @isWebchat()
              onSuccess({
                event_detail: {
                  event_type: 'webchat_finalized',
                  user_full_name: @_currentUserFullName(),
                  event_date: moment().format()
                },
                success: true
              })
            else
              onSuccess(response)
        else
          @trigger('ticket:saved:error', response)
      error: (model, response) =>
        @trigger('ticket:saved:error', response)
    )
    @toJSON = toJsonMethod

  _onUpdateTicketSuccess: (response) ->
    @updateLockVersion(response.lock_version)
    @updateStatus(response.status)
    @updateAssignedToName(response.assigned_to_name)
    @updateCanUpdate(response.can_update)
    @updateCanReply(response.can_reply)
    @updateCanClose(response.can_close)
    @loadCurrentUserAppointment()

  postWithCallbacks:(url, params, callbacks) ->
    #this method is intended to work with formData() thus the contentType and processData parameter prescense is necessary
    $.ajax
      type: 'POST'
      url: url
      data: params
      cache: false
      contentType: false
      processData: false
      success: (response) =>
        if response.success
          callbacks.success(response) if callbacks && callbacks.success
        else
          callbacks.error(response) if callbacks && callbacks.error
      error: (response) =>
        callbacks.error(response) if callbacks && callbacks.error

  jsonPostWithCallbacks:(url, params, callbacks) ->
    $.ajax
      type: 'POST'
      url: url
      data: params
      cache: false
      success: (response) =>
        if response.success
          callbacks.success(response) if callbacks && callbacks.success
        else
          callbacks.error(response) if callbacks && callbacks.error
      error: (response) =>
        callbacks.error(response) if callbacks && callbacks.error

  _doResponse: (url, params, callbacks) ->
    $.ajax
      type: 'POST'
      url: url
      data: params
      cache: false
      contentType: false
      processData: false
      success: (response) =>
        if response.success
          callbacks.success(response) if callbacks && callbacks.success
          @trigger('response:success', response)
          @updateInteractionCount(1) unless response.skip_interactions_count_update
          @updateMetrics(response)
        else
          callbacks.error(response) if callbacks && callbacks.error
          if response.error_code == 'stale_dialogue'
            @trigger('response:staleDialogue')
            swal({
                title: I18n.t('customer.dashboard.conversations.stale_dialogue.message')
                type: 'warning'
                showCancelButton: true
                confirmButtonClass: 'btn-success'
                confirmButtonText: I18n.t('customer.dashboard.conversations.stale_dialogue.refresh')
                cancelButtonText: I18n.t('customer.dashboard.conversations.stale_dialogue.dismiss')
                closeOnConfirm: true
              }, =>
                @updateWithJson(JSON.parse(response.updated_ticket))
            )
          else
            @trigger('response:error', response)
      error: (response) =>
        callbacks.error(response) if callbacks && callbacks.error

  updateWithJson: (response) =>
    if @isWhatsapp()
      interactionsAlreadyPresent = @interactionsAlreadyPresent(response.dialogue.interactions)
      responseInteractionsToBeReplaced = @responseInteractionsToBeReplaced(response.dialogue.interactions)
      response.dialogue['interactions'] = _.without.apply(_, [response.dialogue.interactions].concat(interactionsAlreadyPresent))
      interactionIncrement = response.dialogue.interactions.length - responseInteractionsToBeReplaced.length
      @updateInteractionCount(interactionIncrement) if interactionIncrement > 0
      @_removeResponseInteractionsFromDialogue(responseInteractionsToBeReplaced) if responseInteractionsToBeReplaced.length > 0
    else
      if @isTwitter()
        # La función interaccionesNotPresentYet parece funcionar mal. Encontré varios problemas debugueandola. El codigo no hace lo que declaran las funciones contenidas en esta
        # Entiendo que lo mejor sería que no existiera, porque sino descarta incorrectamente las interacciones correspondientes a las respuestas que se acaban de generar del ticket. Es decir que cuando el contenido entra por la integración y genera una nueva interacción que reemplaza a la ResponseInteraction, esta sustitución no termina realizándose del lado del cliente porque la interacción es descartada por esta función. Si evitamos que se ejecute, posteriormente en el código reemplaza correctamente una interacción por la otra. 
        # Dejo este cambio solo para Twitter porque estaba teniendo problemas al integrar la v2 de la api. No quiero arriesgarme a cambiar el comportamiento de las otras integraciones ahora.
      else
        response.dialogue['interactions'] = @interactionsNotPresentYet(response.dialogue.interactions)
      @updateInteractionCount(response.dialogue.interactions.length)
    response.status_events = @_statusEventsNotPresentYet(response.status_events)
    response.dialogue.has_more = false
    @parseNewContent(response)
    @updateMetrics(response)

  publicResponse: (params) ->
    @_doResponse("/customer/ticket/reply/interaction", params)

  privateResponse: (params) ->
    @_doResponse("/customer/ticket/reply/interaction", params)

  publicResponseWithCallback: (params, callbacks={}) ->
    @_doResponse("/customer/ticket/reply/interaction", params, callbacks)

  privateResponseWithCallback: (params, callbacks={}) ->
    @_doResponse("/customer/ticket/reply/interaction", params, callbacks)

  scheduleResponse: (params, callbacks) ->
    @_doResponse("/customer/ticket/reply/schedule", params, callbacks)

  operatorIsTyping: ->
    $.ajax
      url: "/actions/webchat/operator_is_typing"
      type: 'POST'
      data: { conversation_id: this.get('id') }

  replyChat: (params, callbacks) ->
    @_doResponse("/actions/chat/reply", params, callbacks)

  replyTicketAction: (service_code, params, callbacks) ->
    @postWithCallbacks("/actions/"+service_code+"/reply", params, callbacks)

  changePriority:(params)->
    params['id'] = this.get('id')
    @jsonPostWithCallbacks('/customer/ticket/change_priority', params, {
      error: (response)=>
        @trigger('ticket:prioritize:success', response.lock_version)
      success: (response)=>
        @trigger('ticket:prioritize:success')
    }
    )

  replyWebchat: (params, callbacks) ->
    $.ajax
      url: "/actions/webchat/reply"
      type: 'POST'
      contentType: false
      processData: false
      data: params
      success: (response) =>
        if response.success
          callbacks.success(response) if callbacks && callbacks.success
          @trigger('response:success', response)
          @updateInteractionCount(1) unless response.skip_interactions_count_update
          @updateMetrics(response)
        else
          callbacks.error(response) if callbacks && callbacks.error
          if response.error_code == 'stale_dialogue'
            @trigger('response:staleDialogue')
            swal({
              title: I18n.t('customer.dashboard.conversations.stale_dialogue.message')
              type: 'warning'
              showCancelButton: true
              confirmButtonClass: 'btn-success'
              confirmButtonText: I18n.t('customer.dashboard.conversations.stale_dialogue.refresh')
              cancelButtonText: I18n.t('customer.dashboard.conversations.stale_dialogue.dismiss')
              closeOnConfirm: true
            }, =>
              @updateWithJson(JSON.parse(response.updated_ticket))
            )
          else if response.error_code == 'webchat_finalized'
            swal({
                title: I18n.t('customer.dashboard.conversations.stale_dialogue.message')
                type: 'warning'
                showCancelButton: false
                confirmButtonClass: 'btn-success'
                confirmButtonText: I18n.t('customer.dashboard.conversations.stale_dialogue.close')
                closeOnConfirm: true
              }, =>
                if response.event_detail
                  _event = new Customer.VisibleEvent(response.event_detail)
                  @updateLockVersion(response.lock_version) if response.lock_version
                  @get('visible_events').push(_event)
                @trigger('close_webchat:success')
            )
          else
            @trigger('response:error', response)
      error: (response) =>
        console.warn(response)
        callbacks.error(response) if callbacks && callbacks.error


  ticketClose:(callbacks) ->
    param = {}
    param['id'] = this.get('id')
    $.ajax
      url: '/customer/ticket/close'
      type: 'POST'
      data: param
      success: (response) =>
        callbacks.success(response) if callbacks && callbacks.success
        @_onUpdateTicketSuccess(response)
      error: (response) =>
        @trigger('ticket:saved:error', response)
        callbacks.error(response) if callbacks && callbacks.error

  ticketPendig:(callbacks, param = {}) ->
    param['id'] = this.get('id')
    $.ajax
      url: '/customer/ticket/pending'
      type: 'POST'
      data: param
      success: (response) =>
        callbacks.success(response) if callbacks && callbacks.success
        @_onUpdateTicketSuccess(response)
      error: (response) =>
        callbacks.error(response) if callbacks && callbacks.error


  closeWebchat: (params, callbacks) ->
    $.ajax
      url: "/actions/webchat/finalize"
      type: 'POST'
      data: params
      success: (resp) =>
        if resp.success || resp.error_code == 'webchat_finalized'
          callbacks.success(resp) if callbacks && callbacks.success
          @addWebchatCloseEvent(resp.event_detail)
          @trigger('close_webchat:success')
        else
          callbacks.error(resp) if callbacks && callbacks.error
      error: (resp) =>
        console.warn(resp)
        callbacks.error(resp) if callbacks && callbacks.error

  addWebchatCloseEvent: (event_detail) ->
    return unless @isWebchat()
    return if @isWebchatFinalized()
    _event = new Customer.VisibleEvent(event_detail)
    @get('visible_events').push(_event)

  open: ->
    Backbone.trigger('opened:ticket', @)

  select: (checked) ->
    @set('selected', checked, silent: true)
    @trigger('ticket:summary:selected', checked)

  getStatus: ->
    @get('status').titleize()

  getCreatedAtString: ->
    @getCreatedAt().toLocaleString()

  getCreatedAt: ->
    Date.create(@get('created_at'))

  getResponsesCount: ->
    unless @stats
      throw new Error('stats not loaded')
    @stats.responses_count

  getLastResponseDateString: ->
    unless @stats
      throw new Error('stats not loaded')
    if @stats.last_response_date == 'never'
      'Never'
    else
      Date.create(@stats.last_response_date).toLocaleString()

  getDateMetrics: ->
    @get('date_metrics') || {}

  updateMetrics: (response) =>
    newMetricsData = response.metrics_data
    if newMetricsData
      oldMetricsData = @getDateMetrics()
      oldMetricsData.first_assignation_date = newMetricsData.first_assignation_date
      oldMetricsData.last_assignation_date = newMetricsData.last_assignation_date
      oldMetricsData.first_of_the_last_interactions_date = newMetricsData.first_of_the_last_interactions_date

  getStats: ->
    @stats

  mustShowTicketsByUser: ->
    @stats && (@stats.opened_last_month_count > 0 || @stats.closed_last_month_count > 0)

  getNotes: ->
    unless @notes
      throw new Error('notes not loaded')
    @notes

  isCut: ->
    @get('ticket_already_cut')

  hasCommunicationBlocked: ->
    @get('communication_blocked')

  mustShowTagsInSummary: ->
    _.contains(@get('visible_attributes_in_summary'), 'tags')

  mustShowGroupsInSummary: ->
    _.contains(@get('visible_attributes_in_summary'), 'groups')

  hasGroups: ->
    !_.isEmpty(@get('groups'))

  getGroups: ->
    @get('groups') || []

  getAmountOfPreviousTickets: ->
    '10'

  updateInteractionCount: (increment) ->
    dialogue = @get('dialogue')
    dialogue.interaction_count += increment
    @set('dialogue', dialogue)

  interactionsNotPresentYet: (interactions_as_json) =>
    _.filter interactions_as_json, (interaction) =>
      return true if interaction?.id and @getResponseScheduleById(interaction) == undefined
      ids = _.without([interaction?.data?.id, interaction?.data?.content?.id], null, undefined)
      return _.every(ids, (id) => @getInteractionByContentId(id) == undefined)

  _statusEventsNotPresentYet: (statusEvents) ->
    return [] unless statusEvents
    currentStatusEventsIds = @get('status_events').map((statusEvent) -> statusEvent.id)
    statusEvents.filter((statusEvent) -> !currentStatusEventsIds.includes(statusEvent.id))

  interactionsAlreadyPresent: (interactions_as_json) =>
    found =[]
    for newInteraction in interactions_as_json
      for interaction in @interactions() by -1
        if interaction.attributes?.content?.getContentId() == newInteraction?.data?.interaction?.content_id && interaction.attributes.responseInteraction == undefined
          found.push(newInteraction)
          break
    found

  responseInteractionsToBeReplaced: (interactions_as_json) =>
    found = []
    for newInteraction in interactions_as_json
      if !newInteraction.data.isBrandContent
        continue
      for interaction in @interactions() by -1
        if  interaction.attributes.responseInteraction == true && interaction.attributes?.content?.getContentId() == newInteraction?.data?.interaction?.content_id
          found.push(interaction)
          break
    found

  loadStats: (interaction, force=false) ->
    if @stats && !force
      @trigger('stats:loaded', @stats)
      return
    $.ajax
      type: 'GET'
      url: "/customer/ticket_stats/"
      cache: false
      contentType: false
      data:
        ticket_id: @id
        type: if interaction then interaction.type else 'no_interaction'
        social_network_user: if interaction then interaction.id else 'no_interaction'
      success: (response) =>
        if response.success
          @stats = response.stats
          @trigger('stats:loaded', @stats)
        else
          console.error("error fetching stats of ticket #{@id}")
      error: (response) =>
        console.error("error fetching stats of ticket #{@id}")

  loadNotes: (force=false) ->
    if @asyncNotes && !force
      @trigger('notes:loaded', @asyncNotes)
      return
    $.ajax
      type: 'GET'
      url: "/customer/ticket_notes/#{@id}"
      cache: false
      contentType: false
      processData: false
      success: (response) =>
        if response.success
          @asyncNotes = response.notes
          @trigger('notes:loaded', @asyncNotes)
        else
          console.error("error fetching stats of ticket #{@id}")
      error: (response) =>
        console.error("error fetching stats of ticket #{@id}")

  loadCurrentUserAppointment: ->
    $.ajax
      type: 'GET'
      url: "/customer/ticket/current_user_appointment/#{@id}"
      success: (response) =>
        if response.success
          # silent: true, to not fire 'changed' event because summary uses it model and it's listen to the
          # change event of this model (bad idea). TODO: Remove silent: true when summaryView use a summary model
          @set('userAppointment', JSON.parse(response.appointment), silent: true)
          @trigger('scheduleLoaded')
        else
          console.error("error fetching ticket appointment")
      error: (response) =>
        console.error("error fetching ticket appointment")

  loadAISummary: (force=false) ->
    if @aiSummaryIsFresh() && !force
      @trigger('aisummary:loaded', @aiSummary)
      return
    $.ajax
      type: 'GET'
      url: "/customer/ticket/#{@id}/ai_summary/"
      cache: false
      contentType: false
      success: (response) =>
        if response.success
          @aiSummary = response.summary
          @amountOfInteractionsUsedForAiSummary = @dialogue().interactions.length
          @trigger('aisummary:loaded', @aiSummary)
        else
          console.error("error fetching AI summary of ticket #{@id}")
      error: (response) =>
        console.error("error fetching AI summary of ticket #{@id}")

  aiSummaryIsFresh: ->
    @aiSummary && (@amountOfInteractionsUsedForAiSummary == @dialogue().interactions.length)

  canUpdate: =>
    @get('can_update')

  canCreateExternalTicket: (identifier) =>
    @get('integrations').get(identifier).canCreateExternalTicket()

  _currentUserFullName: =>
    Keepcon.currentUser.first_name + ' ' + Keepcon.currentUser.last_name

  _oldestResponseInteraction: ->
    matched = null
    try
      count = 0
      for interaction in @interactions() by -1
        if count > @MAX_INTERACTIONS_REVERSE_ITERATION
          break
        else if interaction.attributes?.responseInteraction? == true
          matched = interaction
        else if matched != null
          break
        count++
    catch error
      jsLogger.error("Error searching _oldestResponseInteraction - Error: #{error}")
    matched

  _removeResponseInteractionsFromDialogue: (responseInteractionsToRemove) ->
    dialogue = @get('dialogue')
    cleanInteractions = _.without.apply(_, [dialogue.interactions].concat(responseInteractionsToRemove));
    #dialogue.interaction_count = cleanInteractions.length
    dialogue.interactions = cleanInteractions
    @set('dialogue', dialogue)

  _serializeStatusEvents: (statusEvents) ->
    statusEvents.map((event) ->
      event.viewType = 'status_event'
      event
    )

  _parseNewStatusEvents: (statusEventsData) ->
    return unless statusEventsData
    new Customer.Ticket.ConversationHistoryEventCollection(statusEventsData)

  _setNewStatusEvents: (newStatusEvents) ->
    updatedStatusEvents = @get('status_events')
    updatedStatusEvents.add(newStatusEvents.models)
    @set('status_events', updatedStatusEvents)

  _setStatusEvents: (statusEventsData) ->
    return unless statusEventsData
    newStatusEvents = @_parseNewStatusEvents(statusEventsData)
    @_setNewStatusEvents(newStatusEvents)
    @get('status_events')
